import React, { useEffect, useMemo } from 'react';
import {
  ActionList,
  ActionListItem,
  Badge,
  Box,
  Button,
  ChipGroup,
  Chip as BladeChip,
  CloseIcon,
  Dropdown,
  DropdownOverlay,
  IconButton,
  InfoIcon,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  SelectInput,
  SparklesIcon,
  Text,
  TextInput,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import type {
  OvertimeFormula,
  OvertimeFormulaFormData,
} from 'components/Settings/AttendanceAndLeavesSettings/types';
import type { OtSalaryComponent } from './hooks/useOtSalaryComponents';
import { uniqueId } from 'utils/Numbers';
import { useAttendanceDefaults } from 'components/Settings/AttendanceAndLeavesSettings/hooks/useAttendanceDefaults';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { HiddenScrollBox } from './styles';
import { FormulaPreview } from './FormulaPreview';
import {
  FORMULA_RATE_TYPE,
  FORMULA_RATE_UNIT,
  MULTIPLIER_LABELS,
  SELECT_ALL_VALUE,
  validationSchema,
} from 'components/Settings/AttendanceAndLeavesSettings/constants';
import { useFormWithProvider } from 'forms';
const PREDEFINED_MULTIPLIERS = ['1', '1.5', '2', '3'];

interface OvertimeFormulaModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (formula: OvertimeFormula, selectedComponentIds?: string[]) => void;
  otComponents: OtSalaryComponent[];
  isSubmitting?: boolean;
  initialFormula?: OvertimeFormula;
}

export const OvertimeFormulaModal = ({
  isOpen,
  onDismiss,
  onSave,
  otComponents,
  isSubmitting = false,
  initialFormula,
}: OvertimeFormulaModalProps) => {
  const payrollMonth = useMemo(
    () => getFormattedDateValue({ date: new Date(), formatString: dateFormats.isoDate }),
    [],
  );
  const { baseDays, baseHours } = useAttendanceDefaults(payrollMonth);
  const { formData, setFieldValue, register, validate } =
    useFormWithProvider<OvertimeFormulaFormData>();

  const {
    rateType,
    multiplier,
    customMultiplierValue,
    flatAmount,
    unit,
    formulaName = '',
    selectedComponentIds = [],
    showDailyAllowance = false,
    dailyAllowanceAmount,
  } = formData;

  const resetForm = () => {
    setFieldValue('rateType', undefined);
    setFieldValue('multiplier', undefined);
    setFieldValue('customMultiplierValue', undefined);
    setFieldValue('flatAmount', undefined);
    setFieldValue('unit', undefined);
    setFieldValue('formulaName', '');
    setFieldValue('selectedComponentIds', []);
    setFieldValue('showDailyAllowance', false);
    setFieldValue('dailyAllowanceAmount', undefined);
  };

  const includedComponents = otComponents.filter((c) => c.includeInOtWages);
  const showComponentSelector = !includedComponents.length;

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (initialFormula) {
      setFieldValue('rateType', initialFormula.rateType);
      setFieldValue('unit', initialFormula.unit);
      setFieldValue('formulaName', initialFormula.name);
      setFieldValue(
        'flatAmount',
        initialFormula.flatAmount != null ? String(initialFormula.flatAmount) : undefined,
      );
      setFieldValue('showDailyAllowance', !!initialFormula.dailyAllowance);
      setFieldValue(
        'dailyAllowanceAmount',
        initialFormula.dailyAllowance != null ? String(initialFormula.dailyAllowance) : undefined,
      );

      if (initialFormula.multiplier) {
        if (PREDEFINED_MULTIPLIERS.includes(initialFormula.multiplier)) {
          setFieldValue('multiplier', initialFormula.multiplier);
        } else {
          setFieldValue('multiplier', 'custom');
          setFieldValue('customMultiplierValue', initialFormula.multiplier);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDismiss = () => {
    resetForm();
    onDismiss();
  };

  const handleSave = () => {
    if (!validate(validationSchema)) return;

    const formula: OvertimeFormula = {
      id: initialFormula?.id ?? uniqueId(),
      name: formulaName.trim(),
      rateType: rateType!,
      multiplier:
        rateType === FORMULA_RATE_TYPE.multiplier
          ? multiplier === 'custom'
            ? customMultiplierValue
            : multiplier
          : undefined,
      flatAmount: rateType === FORMULA_RATE_TYPE.flat ? Number(flatAmount) : undefined,
      unit: unit!,
      dailyAllowance:
        showDailyAllowance && dailyAllowanceAmount ? Number(dailyAllowanceAmount) : undefined,
      isActive: true,
    };

    const componentsToSave =
      showComponentSelector && rateType === FORMULA_RATE_TYPE.multiplier
        ? selectedComponentIds
        : undefined;

    onSave(formula, componentsToSave);
  };

  const allSelected =
    otComponents.length > 0 && selectedComponentIds.length === otComponents.length;

  const handleComponentChange = ({ values }: { values: string[] }) => {
    const hasSelectAll = values.includes(SELECT_ALL_VALUE);
    const filteredValues = values.filter((v) => v !== SELECT_ALL_VALUE);

    if (hasSelectAll) {
      if (allSelected) {
        setFieldValue('selectedComponentIds', filteredValues);
      } else {
        setFieldValue(
          'selectedComponentIds',
          otComponents.map((c) => c.id),
        );
      }
    } else if (allSelected && filteredValues.length === otComponents.length) {
      setFieldValue('selectedComponentIds', []);
    } else {
      setFieldValue('selectedComponentIds', filteredValues ?? []);
    }
  };

  const generateFormulaName = () => {
    const unitLabel = unit === FORMULA_RATE_UNIT.per_hour ? 'Hourly' : 'Daily';

    if (rateType === FORMULA_RATE_TYPE.flat && flatAmount) {
      setFieldValue(
        'formulaName',
        `Flat ₹${flatAmount}/${unit === FORMULA_RATE_UNIT.per_hour ? 'hr' : 'day'}`,
      );
      return;
    }

    if (rateType === FORMULA_RATE_TYPE.multiplier && multiplier) {
      const effectiveMultiplier = multiplier === 'custom' ? customMultiplierValue : multiplier;
      if (!effectiveMultiplier) return;
      const base = MULTIPLIER_LABELS[effectiveMultiplier] ?? `${effectiveMultiplier}x Overtime`;
      const suffix =
        showDailyAllowance && dailyAllowanceAmount
          ? ` + ${dailyAllowanceAmount} Daily Allowance`
          : '';
      setFieldValue('formulaName', `${base} (${unitLabel})${suffix}`);
      return;
    }

    setFieldValue('formulaName', 'OT Formula');
  };

  const activeComponentIds = showComponentSelector
    ? selectedComponentIds
    : includedComponents.map((c) => c.id);

  const previewComponents = activeComponentIds
    .map((id) => otComponents.find((c) => c.id === id))
    .filter(Boolean) as OtSalaryComponent[];

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size="large"
      accessibilityLabel="Set overtime rates">
      <ModalHeader
        title={initialFormula ? 'Update overtime formula' : 'Set overtime rates'}
        subtitle="Choose how overtime pay should be calculated for your employees."
      />
      <ModalBody padding="spacing.0" height="100%">
        <Box display="flex" height="100%" minHeight="480px" overflow="hidden">
          <Box flex="1" minWidth="0px" display="flex" flexDirection="column" overflow="hidden">
            <HiddenScrollBox>
              <ChipGroup
                accessibilityLabel="Select rate type"
                label="Rate type"
                selectionType="single"
                {...register('rateType', 'chipgroup', 'single')}
                size="small">
                <BladeChip value="multiplier">Multiplier</BladeChip>
                <BladeChip value="flat">Flat rate</BladeChip>
              </ChipGroup>

              {showComponentSelector && rateType === FORMULA_RATE_TYPE.multiplier && (
                <Box marginTop="spacing.7">
                  <Dropdown selectionType="multiple">
                    <SelectInput
                      label="Overtime salary components"
                      placeholder="Select components"
                      onChange={handleComponentChange}
                      value={
                        allSelected
                          ? [...selectedComponentIds, SELECT_ALL_VALUE]
                          : selectedComponentIds
                      }
                    />
                    <DropdownOverlay>
                      <ActionList>
                        <ActionListItem
                          key={SELECT_ALL_VALUE}
                          title="Select All"
                          value={SELECT_ALL_VALUE}
                          isSelected={allSelected}
                        />
                        {otComponents.map((comp) => (
                          <ActionListItem
                            key={comp.id}
                            title={comp.name}
                            description={comp.description ?? undefined}
                            value={comp.id}
                            isSelected={selectedComponentIds.includes(comp.id)}
                          />
                        ))}
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>
                </Box>
              )}

              {!showComponentSelector &&
                rateType === FORMULA_RATE_TYPE.multiplier &&
                includedComponents.length > 0 && (
                  <Box marginTop="spacing.7">
                    <TextInput
                      label="Overtime salary components"
                      value={includedComponents.map((c) => c.name).join(', ')}
                      isDisabled={true}
                      helpText="Shared across all multiplier formulas. Edit from the Overtime Policy section."
                    />
                  </Box>
                )}

              {rateType === FORMULA_RATE_TYPE.multiplier && (
                <Box marginTop="spacing.7">
                  <ChipGroup
                    accessibilityLabel="Select multiplier"
                    label="Multiplier"
                    selectionType="single"
                    {...register('multiplier', 'chipgroup', 'single')}
                    size="small"
                    helpText="As per the Factories Act, the minimum OT rate is 2× the ordinary wage.">
                    <BladeChip value="1">1×</BladeChip>
                    <BladeChip value="1.5">1.5×</BladeChip>
                    <BladeChip value="2">2×</BladeChip>
                    <BladeChip value="3">3×</BladeChip>
                    <BladeChip value="custom">Custom</BladeChip>
                  </ChipGroup>
                  {multiplier === 'custom' && (
                    <Box marginTop="spacing.5" width="200px">
                      <TextInput
                        {...register('customMultiplierValue')}
                        label="Custom value"
                        placeholder="e.g. 4"
                        suffix="×"
                      />
                    </Box>
                  )}
                </Box>
              )}

              {rateType === FORMULA_RATE_TYPE.flat && (
                <Box marginTop="spacing.7" width="200px">
                  <TextInput
                    {...register('flatAmount')}
                    label="Amount"
                    placeholder="e.g. 500"
                    prefix="₹"
                  />
                </Box>
              )}

              {rateType && (
                <Box marginTop="spacing.7">
                  <ChipGroup
                    accessibilityLabel="Select pay unit"
                    label="Pay overtime per"
                    selectionType="single"
                    {...register('unit', 'chipgroup', 'single')}
                    size="small">
                    <BladeChip value={FORMULA_RATE_UNIT.per_hour}>Hour</BladeChip>
                    <BladeChip value={FORMULA_RATE_UNIT.per_day}>Day</BladeChip>
                  </ChipGroup>
                </Box>
              )}

              {rateType === FORMULA_RATE_TYPE.multiplier &&
                unit === FORMULA_RATE_UNIT.per_hour &&
                !showDailyAllowance && (
                  <Box marginTop="spacing.5">
                    <Link
                      icon={PlusIcon}
                      iconPosition="left"
                      size="medium"
                      onClick={() => setFieldValue('showDailyAllowance', true)}>
                      Daily allowance
                    </Link>
                  </Box>
                )}

              {rateType === FORMULA_RATE_TYPE.multiplier &&
                unit === FORMULA_RATE_UNIT.per_hour &&
                showDailyAllowance && (
                  <Box
                    marginTop="spacing.5"
                    padding="spacing.5"
                    borderRadius="medium"
                    borderWidth="thin"
                    borderColor="surface.border.gray.subtle"
                    backgroundColor="surface.background.gray.moderate">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      marginBottom="spacing.4">
                      <Box display="flex" alignItems="center" gap="spacing.2">
                        <Badge color="notice" size="medium">
                          + Daily
                        </Badge>
                        <Tooltip content="A flat amount added on top of the multiplier rate, once per day the employee works overtime">
                          <TooltipInteractiveWrapper>
                            <InfoIcon size="medium" color="surface.icon.gray.muted" />
                          </TooltipInteractiveWrapper>
                        </Tooltip>
                      </Box>
                      <IconButton
                        icon={CloseIcon}
                        accessibilityLabel="Remove daily allowance"
                        emphasis="subtle"
                        size="small"
                        onClick={() => {
                          setFieldValue('showDailyAllowance', false);
                          setFieldValue('dailyAllowanceAmount', undefined);
                        }}
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap="spacing.3">
                      <Box width="150px">
                        <TextInput
                          {...register('dailyAllowanceAmount')}
                          placeholder="e.g. 200"
                          prefix="₹"
                          accessibilityLabel="Daily allowance amount"
                        />
                      </Box>
                      <Text size="medium" color="surface.text.gray.muted">
                        per day worked
                      </Text>
                    </Box>
                  </Box>
                )}

              {rateType && (
                <Box marginTop="spacing.7">
                  <TextInput
                    {...register('formulaName')}
                    label="Formula name"
                    placeholder="e.g. Weekday OT"
                    trailingButton={
                      <Link icon={SparklesIcon} onClick={generateFormulaName}>
                        Auto-generate
                      </Link>
                    }
                  />
                </Box>
              )}
            </HiddenScrollBox>
          </Box>
          <FormulaPreview
            rateType={rateType}
            flatAmount={flatAmount ? Number(flatAmount) || 0 : undefined}
            multiplier={multiplier === 'custom' ? customMultiplierValue : multiplier}
            unit={unit}
            dailyAllowanceAmount={
              dailyAllowanceAmount ? Number(dailyAllowanceAmount) || 0 : undefined
            }
            showDailyAllowance={showDailyAllowance}
            previewComponents={previewComponents}
            baseDays={baseDays || '26'}
            baseHours={baseHours || '8'}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
          <Button variant="tertiary" size="medium" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button variant="primary" size="medium" isLoading={isSubmitting} onClick={handleSave}>
            {initialFormula ? 'Update Formula' : 'Save Formula'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
